import { Input } from '@nike/frame-component-library';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';

import AutoPositioner from './AutoPositioner';
import Calendar from './Calendar';

import './DatePicker.css';

const ESCAPE_KEY_CODE = 27;
const START = 'start';
const END = 'end';
export const DEFAULT_EMPTY_PLACEHOLDER_DISPLAY = 'Click To Select A Date';

export const getDateDisplay = (start, end, withTime, withRange, placeholder) => {
  if (start) {
    if (withRange) {
      const format = withTime ? 'MM/DD/YY h:mm A' : 'MMM Do YYYY';
      return `${start ? start.format(format) : ''} - ${end ? end.format(format) : ''}`;
    }
    if (withTime) {
      return start.format('ddd, MMMM Do YYYY, h:mm A');
    }
    return start.format('ddd, MMMM Do YYYY');
  }
  return placeholder;
};

const getCalendarDisplayMonth = (startOrEnd, start, end) => {
  if (startOrEnd === END) {
    if (end && !end.isSame(start, 'month')) {
      return end;
    } else if (start) {
      return start.clone().add(1, 'month');
    }
    // end calendar should be next month when no start is provided
    return moment().add(1, 'month');
  }
  // return current month when no date provided
  return start || moment();
};

class DatePicker extends React.Component {
  constructor(props) {
    super(props);
    this.handleOpenCalendar = this.handleOpenCalendar.bind(this);
    this.handleCancelSelection = this.handleCancelSelection.bind(this);
    this.handleCloseCalendarClick = this.handleCloseCalendarClick.bind(this);
    this.handleCloseCalendar = this.handleCloseCalendar.bind(this);

    this.state = {
      isActive: false,
    };
  }

  componentWillUnmount() {
    document.body.removeEventListener('click', this.handleCloseCalendarClick, false);
  }

  handleOpenCalendar() {
    const { isDisabled } = this.props;
    if (!isDisabled) {
      document.body.addEventListener('click', this.handleCloseCalendarClick, false);
      this.setState({ isActive: true });
    }
  }

  handleCloseCalendar() {
    document.body.removeEventListener('click', this.handleCloseCalendarClick, false);
    this.setState({ isActive: false });
  }

  handleCancelSelection(event) {
    const { handleCancelSetDate } = this.props;
    if (event.keyCode === ESCAPE_KEY_CODE) {
      handleCancelSetDate();
      this.handleCloseCalendar();
    }
  }

  handleCloseCalendarClick(event) {
    const {
      enableRange, errorMessage, handleCancelSetDate, handleOutsideClick, selectedDates,
    } = this.props;
    const { isActive } = this.state;
    if (!this.node.contains(event.target)) {
      event.stopPropagation();
      if (isActive && !errorMessage) {
        if (enableRange && !selectedDates.end) {
          handleCancelSetDate();
        } else {
          handleOutsideClick();
        }
        this.handleCloseCalendar();
      }
    }
  }

  render() {
    const {
      className,
      earliestDateTime,
      selectedDates,
      enableOutsideDays,
      enableRange,
      enableTime,
      errorMessage,
      handleUpdateDate,
      handleUpdateHours,
      handleUpdateMinutes,
      latestDateTime,
      isDisabled,
      placeholder,
      zIndex,
    } = this.props;
    const { isActive } = this.state;
    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div ref={(node) => { this.node = node; }} className={`datepicker-container ${className}`} onKeyDown={this.handleCancelSelection}>
        <Input
          isReadOnly
          errorMessage={typeof errorMessage === 'string' ? errorMessage : ''}
          id="datepicker-input"
          inputClassName="ta-sm-c u-cursor-pointer"
          isDisabled={isDisabled}
          value={getDateDisplay(selectedDates.start, selectedDates.end, enableTime, enableRange, placeholder)}
          onClick={this.handleOpenCalendar}
        />
        {isActive && (
          <AutoPositioner>
            <div className={`datepicker-overlay ta-sm-c z${zIndex} center-picker`} id="fcl-datepicker-overlay">
              <Calendar
                calendarIdentifier={START}
                calendarIdentifierEnd={END}
                displayedMonth={getCalendarDisplayMonth(START, selectedDates.start)}
                earliestDateTime={earliestDateTime}
                enableOutsideDays={enableOutsideDays}
                enableRange={enableRange}
                enableTime={enableTime}
                handleUpdateDate={handleUpdateDate}
                handleUpdateHours={handleUpdateHours}
                handleUpdateMinutes={handleUpdateMinutes}
                latestDateTime={latestDateTime}
                selectedDates={selectedDates}
              />
            </div>
          </AutoPositioner>
        )}
      </div>
    );
  }
}

DatePicker.propTypes = {
  // eslint-disable-next-line react/no-unused-prop-types
  calendarIdentifier: PropTypes.string,
  className: PropTypes.string.isRequired,
  earliestDateTime: PropTypes.shape(),
  enableOutsideDays: PropTypes.bool,
  enableRange: PropTypes.bool,
  enableTime: PropTypes.bool,
  errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  handleCancelSetDate: PropTypes.func,
  handleOutsideClick: PropTypes.func,
  handleUpdateDate: PropTypes.func,
  handleUpdateHours: PropTypes.func,
  handleUpdateMinutes: PropTypes.func,
  isDisabled: PropTypes.bool,
  latestDateTime: PropTypes.shape(),
  placeholder: PropTypes.string,
  selectedDates: PropTypes.exact({
    end: PropTypes.shape(),
    start: PropTypes.shape(),
  }),
  zIndex: PropTypes.number,
};

DatePicker.defaultProps = {
  calendarIdentifier: null,
  earliestDateTime: null,
  enableOutsideDays: false,
  enableRange: false,
  enableTime: false,
  errorMessage: null,
  handleCancelSetDate: () => null,
  handleOutsideClick: () => null,
  handleUpdateDate: () => null,
  handleUpdateHours: () => null,
  handleUpdateMinutes: () => null,
  isDisabled: false,
  latestDateTime: null,
  placeholder: DEFAULT_EMPTY_PLACEHOLDER_DISPLAY,
  selectedDates: {},
  zIndex: 1,
};

export default DatePicker;
